import { Button, TextField } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useMemo, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { TbArrowBackUp, TbX } from 'react-icons/tb';
import { invalidateTerritoriesCache } from '../../custom-components/QueryEditor';
import { toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { US_STATE_NAMES } from '../../utils/UsStates';
import { useNavigatorStore } from '../../zustand/useNavigatorStore';

/** A state's name by its code; DC is not among the app's state names. */
export const stateName = (code: string) =>
  US_STATE_NAMES[code] ?? (code === 'DC' ? 'District of Columbia' : code);

interface TerritoryDetailProps {
  territory: Edulead.EduleadV4Territory;
  /** Whether the form is in view, not sliding out: only then do its state rows edit. */
  isOpen: boolean;
  onBack: () => void;
  /** Called with the territory a rename returned. */
  onChanged: (territory: Edulead.EduleadV4Territory) => void;
}

/**
 * One territory's form: its name, and its states as edited on the map since it was selected or
 * last saved, each kept, added or (struck through) removed. Nothing reaches the API until Save.
 */
export function TerritoryDetail({ territory, isOpen, onBack, onChanged }: TerritoryDetailProps) {
  const [name, setName] = useState(territory.name ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const editingTerritoryId = useNavigatorStore((state) => state.editingTerritoryId);
  const edited = useNavigatorStore((state) => state.territoryStates);
  const saved = useNavigatorStore((state) => state.territorySavedStates);
  const isSavingEdits = useNavigatorStore((state) => state.isSavingTerritory);
  const paint = useNavigatorStore((state) => state.paintTerritory);

  const canEdit = isOpen && editingTerritoryId === territory.id;

  // The states by name, saved and edited together.
  const rows = useMemo(() => {
    const isEdited = editingTerritoryId === territory.id && saved !== null;
    const savedSet = new Set(isEdited ? saved : (territory.states ?? []));
    const editedSet = new Set(isEdited ? edited : savedSet);
    return [...new Set([...editedSet, ...savedSet])]
      .map((code) => ({
        code,
        name: stateName(code),
        status: !savedSet.has(code) ? 'added' : !editedSet.has(code) ? 'removed' : 'kept',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [edited, saved, territory.states, editingTerritoryId]);
  const added = rows.filter((r) => r.status === 'added').length;
  const removed = rows.filter((r) => r.status === 'removed').length;
  const count = rows.length - removed;

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setName(territory.name ?? '');
      return;
    }
    if (trimmed === territory.name || !territory.id) return;
    try {
      setIsSaving(true);
      const res = await eduleadV4.territories.update({
        id: territory.id,
        update_mask: 'name',
        body: { name: trimmed },
      });
      invalidateTerritoriesCache();
      if (res.territory) onChanged(res.territory);
    } catch (error) {
      toastV4Error(error, 'Failed to rename territory');
      setName(territory.name ?? '');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="beacon-detail">
      <button
        type="button"
        className="navigator-beacons-panel__bar beacon-detail__back"
        onClick={onBack}
      >
        <FaArrowLeft size={11} /> Territories
      </button>

      <div className="beacon-detail__body">
        <div className="beacon-detail__section">
          <div className="beacon-detail__section-title">Name</div>
          <TextField
            label="Name"
            labelHidden
            size="small"
            value={name}
            isDisabled={isSaving}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
        </div>

        <div className="beacon-detail__section">
          <div className="beacon-detail__section-title beacon-detail__count">
            States ({count.toLocaleString()})
            {(added > 0 || removed > 0) && (
              <span className="beacon-detail__diff">
                {added > 0 && <span className="beacon-detail__diff--added">+{added}</span>}
                {removed > 0 && <span className="beacon-detail__diff--removed">−{removed}</span>}
              </span>
            )}
          </div>
          {rows.length > 0 && (
            <div className="beacon-detail__list">
              {rows.map((row) => (
                <div
                  key={row.code}
                  className={`beacon-detail__row beacon-detail__row--${row.status}`}
                >
                  <span className="beacon-detail__row-name">{row.name}</span>
                  {canEdit && !isSavingEdits && (
                    <Button
                      size="small"
                      className="nohover tiny beacon-detail__row-action"
                      title={
                        row.status === 'removed'
                          ? 'Keep in the territory'
                          : 'Remove from the territory'
                      }
                      onClick={() =>
                        paint([row.code], row.status === 'removed' ? 'Added' : 'Removed')
                      }
                    >
                      {row.status === 'removed' ? <TbArrowBackUp /> : <TbX />}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
